import React from 'react';
import { X, Plus, Minus, Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet';
import { trackEvent } from './TrackingPixels';

const CartSidebar = () => {
  const { cart, removeFromCart, updateQuantity, getCartTotal, isCartOpen, setIsCartOpen } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    trackEvent.beginCheckout(getCartTotal());
    setIsCartOpen(false);
    navigate('/checkout');
  };

  const cartTotal = getCartTotal();
  const deliveryCharge = cartTotal > 5000 ? 0 : 99;
  const finalTotal = cartTotal + deliveryCharge;

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-xl">
            <ShoppingBag className="text-blue-700" size={24} />
            Shopping Cart ({cart.length})
          </SheetTitle>
        </SheetHeader>

        {cart.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[60vh] text-center">
            <ShoppingBag size={80} className="text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Your cart is empty</h3>
            <p className="text-gray-600 mb-6">Add products to your cart to continue shopping</p>
            <Button
              onClick={() => {
                setIsCartOpen(false);
                navigate('/products');
              }}
              className="bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700"
            >
              Browse Products
            </Button>
          </div>
        ) : (
          <div className="mt-8 space-y-6">
            {/* Cart Items */}
            <div className="space-y-4">
              {cart.map((item) => (
                <div key={item.id} className="flex gap-4 bg-gray-50 p-4 rounded-lg">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-20 h-20 object-cover rounded-md"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">{item.name}</h4>
                    {item.brand && (
                      <p className="text-xs text-gray-600 mb-2">{item.brand}</p>
                    )}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-md">
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1.5 hover:bg-gray-100 rounded-l-md transition-colors"
                        >
                          <Minus size={14} />
                        </button>
                        <span className="px-3 font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1.5 hover:bg-gray-100 rounded-r-md transition-colors"
                        >
                          <Plus size={14} />
                        </button>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded transition-colors"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-blue-700">₹{(item.price * item.quantity).toLocaleString()}</p>
                    {item.quantity > 1 && (
                      <p className="text-xs text-gray-600">₹{item.price} each</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Cart Summary */}
            <div className="border-t border-gray-200 pt-4 space-y-3">
              <div className="flex justify-between text-gray-700">
                <span>Subtotal</span>
                <span className="font-semibold">₹{cartTotal.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-gray-700">
                <span>Delivery Charges</span>
                <span className="font-semibold">
                  {deliveryCharge === 0 ? (
                    <span className="text-green-600">FREE</span>
                  ) : (
                    `₹${deliveryCharge}`
                  )}
                </span>
              </div>
              {cartTotal < 5000 && (
                <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  Add ₹{(5000 - cartTotal).toLocaleString()} more for FREE delivery!
                </p>
              )}
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-3 border-t border-gray-200">
                <span>Total</span>
                <span className="text-blue-700">₹{finalTotal.toLocaleString()}</span>
              </div>
            </div>

            {/* Checkout Button */}
            <Button
              onClick={handleCheckout}
              className="w-full bg-gradient-to-r from-blue-700 to-blue-600 hover:from-blue-800 hover:to-blue-700 text-white py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="checkout-btn"
            >
              Proceed to Checkout
              <ArrowRight size={20} className="ml-2" />
            </Button>

            <p className="text-center text-xs text-gray-500 mt-1">Cash on Delivery available</p>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-3 text-center text-xs text-gray-600 pt-4 border-t border-gray-200">
              <div className="flex items-center justify-center gap-2">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <span>Free Installation</span>
              </div>
              <div className="flex items-center justify-center gap-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  ✓
                </div>
                <span>1 Year Warranty</span>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSidebar;
